import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Calendar, TrendingUp, TrendingDown, AlertTriangle, Phone, Store, Target, Clock, Building2, Eye, Filter, BarChart3, UserX, AlertCircle, Users, ClipboardCheck, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import GymDrilldownModal from '@/components/GymDrilldownModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useWtfLiveData } from '@/hooks/useWtfLiveData';
import { WtfDataColumn, DiscrepancyBadge, WtfSyncBadge } from '@/components/wtf';

const ProblemBadges = ({ problems = [] }) => {
  const badgeConfig = {
    missed_pulse: { icon: Clock, label: 'Missed', className: 'bg-red-100 text-red-700 border-red-200' },
    no_fd: { icon: UserX, label: 'No FD', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    low_connect: { icon: TrendingDown, label: 'Low %', className: 'bg-red-100 text-red-700 border-red-200' },
    pending_verification: { icon: AlertCircle, label: 'Pending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
  };

  if (!problems || problems.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {problems.map(p => {
        const config = badgeConfig[p];
        if (!config) return null;
        const Icon = config.icon;
        return (
          <Badge key={p} variant="outline" className={`text-xs py-0 ${config.className}`}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      })}
    </div>
  );
};

/** Get today's date in IST (YYYY-MM-DD) — matches backend Asia/Kolkata timezone */
const getISTDate = () => {
  const now = new Date();
  const istMs = now.getTime() + (5.5 * 60 * 60 * 1000);
  return new Date(istMs).toISOString().split('T')[0];
};

export default function CommandCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getISTDate());
  const [drilldownModal, setDrilldownModal] = useState({ open: false, gymId: null });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'team_leader';

  // Role-specific insights state
  const [roleInsights, setRoleInsights] = useState({
    // Manager insights
    tlPerformance: [],
    pendingVerifications: 0,
    problemGyms: 0,
    topPerformer: null,
    needsAttention: null,
    // TL insights
    myGyms: [],
    myTargetProgress: { closures: 0, target: 0 },
    myPendingActions: 0,
    myPulseStatus: { filled: 0, total: 0 }
  });

  // Commitment stats state
  const [commitmentStats, setCommitmentStats] = useState({
    pending: 0,
    total: 0
  });

  // Commitment insights state (for comprehensive view)
  const [commitmentInsights, setCommitmentInsights] = useState({
    totalExpected: 0,
    fulfilled: 0,
    conversionRate: 0,
    revenueExpected: 0,
    revenueDone: 0,
    verifiedByTL: 0,
    verifiedByManager: 0,
    fullyVerified: 0,
    unverified: 0,
    pendingByManager: {}, // { "Vikram": 5, "Deepti": 3 }
    // Today vs Past breakdown
    todayCount: 0,
    pastCount: 0,
    todayPendingVerification: 0,
    pastPendingVerification: 0
  });

  // WTF Live Data hooks — real API feeds
  const { data: wtfTargets, lastSync: wtfTargetsSync, stale: wtfTargetsStale, loading: wtfTargetsLoading } = useWtfLiveData('targets');
  const { data: wtfCheckinStats, lastSync: wtfCheckinSync, stale: wtfCheckinStale, loading: wtfCheckinLoading } = useWtfLiveData('checkin_stats');
  const { data: wtfLeadStats, lastSync: wtfLeadsSync, stale: wtfLeadsStale, loading: wtfLeadsLoading } = useWtfLiveData('lead_stats');
  const { data: wtfMemberCount, lastSync: wtfMemberSync, stale: wtfMemberStale, loading: wtfMemberLoading } = useWtfLiveData('member_count');

  // Filter states
  const [managers, setManagers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedTL, setSelectedTL] = useState('all');
  const [selectedGym, setSelectedGym] = useState('all');

  // Fetch master data for filters
  const fetchMasterData = async () => {
    try {
      const [managersRes, tlRes, gymsRes] = await Promise.all([
        axiosInstance.get('/managers'),
        axiosInstance.get('/team-leaders'),
        axiosInstance.get('/gyms')
      ]);
      setManagers(managersRes.data);
      setTeamLeaders(tlRes.data);
      setGyms(gymsRes.data.filter(g => g.status === 'Active'));
    } catch (error) {
      console.error('Failed to load master data');
    }
  };

  const fetchData = async (date = '') => {
    try {
      setLoading(true);
      const url = date ? `/command-center?date=${date}` : '/command-center';
      const response = await axiosInstance.get(url);
      setData(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to load command center data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch role-specific insights
  const fetchRoleInsights = async () => {
    try {
      if (userRole === 'admin' || userRole === 'manager') {
        // Manager insights: TL performance comparison, pending verifications, problem gyms
        const dashboardRes = await axiosInstance.get('/manager-dashboard', { params: { date: selectedDate } });

        const teamPerf = dashboardRes.data?.teamPerformance || [];
        const sortedByClosures = [...teamPerf].sort((a, b) => (b.closures || 0) - (a.closures || 0));
        const sortedByConnect = [...teamPerf].sort((a, b) => (a.connectRate || 0) - (b.connectRate || 0));

        setRoleInsights(prev => ({
          ...prev,
          tlPerformance: teamPerf,
          pendingVerifications: dashboardRes.data?.pendingVerifications || 0,
          problemGyms: dashboardRes.data?.problemGyms || 0,
          topPerformer: sortedByClosures[0] || null,
          needsAttention: sortedByConnect.find(tl => (tl.connectRate || 0) < 60) || null
        }));
      }

      if (userRole === 'team_leader') {
        // TL insights: My gyms, targets, pending actions, pulse status
        const [tlDashRes] = await Promise.all([
          axiosInstance.get('/views/tl', { params: { date: selectedDate } })
        ]);

        const myGyms = tlDashRes.data?.gyms || [];
        const totalClosures = myGyms.reduce((sum, g) => sum + (g.closures || 0), 0);
        const totalTarget = myGyms.length * 5; // 5 closures per gym target
        const pulsesFilled = myGyms.reduce((sum, g) => sum + (g.pulses_filled || 0), 0);
        const totalPulses = myGyms.length * 4; // 4 pulses per gym

        setRoleInsights(prev => ({
          ...prev,
          myGyms: myGyms,
          myTargetProgress: { closures: totalClosures, target: totalTarget },
          myPendingActions: tlDashRes.data?.pendingActions || myGyms.filter(g => (g.problems || []).length > 0).length,
          myPulseStatus: { filled: pulsesFilled, total: totalPulses }
        }));
      }
    } catch (error) {
      console.error('Failed to fetch role insights', error);
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
    const interval = setInterval(() => fetchData(selectedDate), 60000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  // Fetch commitment stats and insights with auto-refresh
  useEffect(() => {
    const fetchCommitmentStats = async () => {
      try {
        // Fetch both commitments list and insights
        const [commitmentsRes, insightsRes] = await Promise.all([
          axiosInstance.get('/commitments', { params: { status: 'pending', date: selectedDate, view_mode: 'both' } }),
          axiosInstance.get('/commitment-insights', { params: { date: selectedDate } }).catch(() => ({ data: null }))
        ]);

        const commitments = commitmentsRes.data?.commitments || [];
        setCommitmentStats({
          pending: commitments.length,
          total: commitments.length
        });

        // Calculate verification breakdown
        let verifiedByTL = 0;
        let verifiedByManager = 0;
        let fullyVerified = 0;
        let unverified = 0;
        const pendingByManager = {};

        // Today vs Past breakdown
        let todayCount = 0;
        let pastCount = 0;
        let todayPendingVerification = 0;
        let pastPendingVerification = 0;
        const today = selectedDate || getISTDate();

        commitments.forEach(c => {
          const tlVerified = c.commitment_tl_verified;
          const mgrVerified = c.commitment_mgr_verified;
          // entry_date is the actual date field used in the API response
          const commitmentDate = c.entry_date || c.date || c.commitment_date;
          const isToday = commitmentDate === today;
          const isPendingVerification = !tlVerified || !mgrVerified;

          // Count today vs past
          if (isToday) {
            todayCount++;
            if (isPendingVerification) todayPendingVerification++;
          } else {
            pastCount++;
            if (isPendingVerification) pastPendingVerification++;
          }

          if (tlVerified && mgrVerified) {
            fullyVerified++;
          } else if (tlVerified && !mgrVerified) {
            verifiedByTL++;
            // Track which manager needs to verify
            const mgrName = c.manager_name || 'Unknown';
            pendingByManager[mgrName] = (pendingByManager[mgrName] || 0) + 1;
          } else if (!tlVerified && mgrVerified) {
            verifiedByManager++;
          } else {
            unverified++;
          }
        });

        // Calculate totals from insights or commitments
        const insights = insightsRes.data || {};
        const totalExpected = insights.totalExpected || commitments.reduce((sum, c) => sum + (c.closure_expected || 0), 0);
        const fulfilled = insights.fulfilled || commitments.reduce((sum, c) => sum + (c.closure_done || 0), 0);
        const revenueExpected = insights.revenueExpected || commitments.reduce((sum, c) => sum + (c.revenue_expected || 0), 0);
        const revenueDone = insights.revenueDone || commitments.reduce((sum, c) => sum + (c.revenue_done || 0), 0);

        setCommitmentInsights({
          totalExpected,
          fulfilled,
          conversionRate: totalExpected > 0 ? Math.round((fulfilled / totalExpected) * 100) : 0,
          revenueExpected,
          revenueDone,
          verifiedByTL,
          verifiedByManager,
          fullyVerified,
          unverified,
          pendingByManager,
          todayCount,
          pastCount,
          todayPendingVerification,
          pastPendingVerification
        });
      } catch (error) {
        console.error('Failed to fetch commitment stats', error);
      }
    };

    // Initial fetch
    fetchCommitmentStats();

    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchCommitmentStats, 120000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [selectedDate]);

  // Fetch role insights when date changes
  useEffect(() => {
    fetchRoleInsights();
  }, [selectedDate, userRole]);

  // Cascading filter logic
  const filteredTLs = useMemo(() => {
    if (selectedManager === 'all') return teamLeaders;
    return teamLeaders.filter(tl => tl.manager_id === selectedManager);
  }, [teamLeaders, selectedManager]);

  const filteredGyms = useMemo(() => {
    let result = gyms;
    if (selectedManager !== 'all') {
      result = result.filter(g => g.manager_id === selectedManager);
    }
    if (selectedTL !== 'all') {
      result = result.filter(g => g.tl_id === selectedTL);
    }
    return result;
  }, [gyms, selectedManager, selectedTL]);

  // Filter gym performance data based on selections
  const filteredGymPerformance = useMemo(() => {
    if (!data?.gym_performance) return [];
    let result = data.gym_performance;
    
    if (selectedManager !== 'all') {
      const managerGyms = gyms.filter(g => g.manager_id === selectedManager).map(g => g.gym_id);
      result = result.filter(gp => managerGyms.includes(gp.gym_id));
    }
    if (selectedTL !== 'all') {
      const tlGyms = gyms.filter(g => g.tl_id === selectedTL).map(g => g.gym_id);
      result = result.filter(gp => tlGyms.includes(gp.gym_id));
    }
    if (selectedGym !== 'all') {
      result = result.filter(gp => gp.gym_id === selectedGym);
    }
    
    return result;
  }, [data?.gym_performance, selectedManager, selectedTL, selectedGym, gyms]);

  // Recalculate metrics based on filtered gyms
  const filteredMetrics = useMemo(() => {
    const gp = filteredGymPerformance;
    const totalDialed = gp.reduce((sum, g) => sum + (g.dialed || 0), 0);
    const totalConnected = gp.reduce((sum, g) => sum + (g.connected || 0), 0);
    const totalClosures = gp.reduce((sum, g) => sum + (g.closures || 0), 0);
    const connectRate = totalDialed > 0 ? Math.round((totalConnected / totalDialed) * 100) : 0;
    
    return {
      total_dialed: totalDialed,
      total_connected: totalConnected,
      connect_rate: connectRate,
      total_closures: totalClosures,
      gym_count: gp.length
    };
  }, [filteredGymPerformance]);

  // WTF aggregate values for Section 1 (Today at a Glance)
  // Real API returns objects (not arrays) for most feeds, with string number values
  const wtfAggregates = useMemo(() => {
    // checkin_stats is an object: {total_member:"0", total_trainer:"0", ...}
    const checkins = wtfCheckinStats || {};
    const totalWtfAttendance = parseInt(checkins.total_member || '0', 10) + parseInt(checkins.total_trainer || '0', 10);

    // lead_stats is an object: {todays_leads:"30", all_leads:"54337", converted_leads:"7750", total_walkins:"71", ...}
    const leads = wtfLeadStats || {};
    const totalWtfLeads = parseInt(leads.todays_leads || '0', 10);
    const totalWtfWalkins = parseInt(leads.total_walkins_today || leads.total_walkins || '0', 10);

    // member_count is an object: {total: 61862}
    const members = wtfMemberCount || {};
    const totalWtfMembers = parseInt(members.total || '0', 10);

    // targets is an array: [{gym_id, gym_name, daily_target, daily_achieved, remaining_target, ...}]
    const targetsArr = Array.isArray(wtfTargets) ? wtfTargets : [];
    const totalWtfTarget = targetsArr.reduce((sum, item) => sum + parseInt(item.daily_target || '0', 10), 0);
    const totalWtfAchieved = targetsArr.reduce((sum, item) => sum + parseInt(item.daily_achieved || '0', 10), 0);

    return {
      leads: totalWtfLeads,
      walkins: totalWtfWalkins,
      members: totalWtfMembers,
      attendance: totalWtfAttendance,
      target: totalWtfTarget,
      closures: totalWtfAchieved,
      connected: 0, // not available from WTF API
    };
  }, [wtfCheckinStats, wtfLeadStats, wtfMemberCount, wtfTargets]);

  // WTF per-gym lookup for Section 3 (Gym Performance)
  // Only `targets` feed has per-gym data; other feeds are aggregate-only
  const wtfGymLookup = useMemo(() => {
    const lookup = {};
    const targetsArr = Array.isArray(wtfTargets) ? wtfTargets : [];
    targetsArr.forEach(item => {
      const key = item.gym_id || item.gym_name;
      if (key) {
        lookup[key] = {
          target: parseInt(item.daily_target || '0', 10),
          achieved: parseInt(item.daily_achieved || '0', 10),
          remaining: parseInt(item.remaining_target || '0', 10),
        };
      }
    });
    return lookup;
  }, [wtfTargets]);

  // WTF role-scoped data for manager/TL summary cards
  // targets feed is the only per-gym feed; leads/checkins/members are aggregate-only
  const wtfRoleData = useMemo(() => {
    // Determine user's gym IDs based on role
    let userGymIds = new Set();
    if (userRole === 'manager' || userRole === 'admin') {
      const managerGyms = gyms.filter(g => g.manager_id === user.user_id || g.manager_id === user._id);
      managerGyms.forEach(g => userGymIds.add(g.gym_id));
    } else if (userRole === 'team_leader') {
      const tlGyms = gyms.filter(g => g.tl_id === user.user_id || g.tl_id === user._id);
      tlGyms.forEach(g => userGymIds.add(g.gym_id));
    }

    if (userGymIds.size === 0) return null;

    // Filter targets (array) by user's gyms
    const targetsArr = Array.isArray(wtfTargets) ? wtfTargets : [];
    const myTargets = targetsArr.filter(item => item.gym_id && userGymIds.has(item.gym_id));

    const totalTarget = myTargets.reduce((s, i) => s + parseInt(i.daily_target || '0', 10), 0);
    const totalAchieved = myTargets.reduce((s, i) => s + parseInt(i.daily_achieved || '0', 10), 0);
    const totalRemaining = myTargets.reduce((s, i) => s + parseInt(i.remaining_target || '0', 10), 0);

    // Aggregate leads/walkins from lead_stats (not per-gym, show total)
    const leads = wtfLeadStats || {};
    const totalWalkins = parseInt(leads.total_walkins_today || leads.total_walkins || '0', 10);

    return {
      totalTarget,
      totalAchieved,
      totalRemaining,
      totalWalkins,
    };
  }, [wtfTargets, wtfLeadStats, gyms, userRole, user]);

  // Reset dependent filters when parent changes
  const handleManagerChange = (value) => {
    setSelectedManager(value);
    setSelectedTL('all');
    setSelectedGym('all');
  };

  const handleTLChange = (value) => {
    setSelectedTL(value);
    setSelectedGym('all');
  };

  const clearFilters = () => {
    setSelectedManager('all');
    setSelectedTL('all');
    setSelectedGym('all');
  };

  const hasActiveFilters = selectedManager !== 'all' || selectedTL !== 'all' || selectedGym !== 'all';

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  const today = data?.today || getISTDate();
  const yesterday = data?.yesterday || '';
  const glance = data?.today_at_glance || {};
  const yesterdayData = data?.yesterday_data || {};
  const pulseStatus = data?.pulse_status || [];
  const alerts = data?.alerts || [];
  const pipeline = data?.pipeline || {};
  const targets = data?.targets || {};

  // Use filtered or original metrics based on filter state
  const displayMetrics = hasActiveFilters ? filteredMetrics : glance;

  // Calculate gaps and trends
  const getGap = (actual, target) => {
    const gap = actual - target;
    return gap >= 0 ? `+${gap}` : `${gap}`;
  };

  const getStatus = (actual, target, isRate = false) => {
    if (isRate) {
      if (actual >= target) return '✅';
      if (actual >= target - 10) return '🟡';
      return '🔴';
    }
    if (actual >= target) return '✅';
    if (actual >= target * 0.8) return '🟡';
    return '🔴';
  };

  const getTrend = (today, yesterday) => {
    if (today > yesterday) return <TrendingUp size={16} className="text-green-600" />;
    if (today < yesterday) return <TrendingDown size={16} className="text-red-600" />;
    return <span className="text-gray-400">—</span>;
  };

  const getStatusColor = (status) => {
    if (status === '✅' || status === '✅ Done' || status === 'Complete') return 'text-green-600';
    if (status === '🟡' || status === '⏳ Pending' || status === 'In Progress') return 'text-yellow-600';
    return 'text-red-600';
  };

  // Chart data preparation
  const gymChartData = filteredGymPerformance.map(g => ({
    name: g.gym?.substring(0, 10) || g.gym_id,
    dialed: g.dialed || 0,
    connected: g.connected || 0,
    closures: g.closures || 0,
    connectPct: g.connect_pct || 0
  }));

  const closureBreakdownData = [
    { name: 'Phone', value: glance.closure_phone || 0, color: '#3B82F6' },
    { name: 'Counter', value: glance.closure_counter || 0, color: '#8B5CF6' }
  ];

  const pulseChartData = pulseStatus.map(p => ({
    name: p.pulse_name,
    dialed: p.dialed || 0,
    connected: p.connected || 0,
    closures: p.closures || 0
  }));

  // Metrics for display (matching Excel spec)
  // wtfKey maps each metric to a wtfAggregates field for WTF Actual column
  const metricsData = [
    { label: 'Total Dialed', actual: displayMetrics.total_dialed || 0, target: targets.total_dialed || 640, yesterday: yesterdayData.total_dialed || 0, wtfKey: 'leads' },
    { label: 'Total Connected', actual: displayMetrics.total_connected || 0, target: targets.total_connected || 448, yesterday: yesterdayData.total_connected || 0, wtfKey: null },
    { label: 'Connect Rate', actual: displayMetrics.connect_rate || 0, target: 70, yesterday: yesterdayData.connect_rate || 0, isRate: true, wtfKey: null },
    { label: 'Visit Scheduled', actual: glance.visit_scheduled || 0, target: targets.visit_scheduled || 160, yesterday: yesterdayData.visit_scheduled || 0, wtfKey: 'walkins' },
    { label: 'Visit Done', actual: glance.visit_done || 0, target: targets.visit_done || 80, yesterday: yesterdayData.visit_done || 0, wtfKey: 'attendance' },
    { label: 'Closure Expected', actual: glance.closure_expected || 0, target: targets.closure_expected || 120, yesterday: 0, wtfKey: 'target' },
    { label: 'Closure Done', actual: glance.total_closures || 0, target: targets.closure_done || 60, yesterday: 0, wtfKey: 'closures' },
    { label: 'Phone Closures', actual: glance.closure_phone || 0, target: targets.closure_phone || 48, yesterday: 0, wtfKey: null },
    { label: 'Counter Closures', actual: glance.closure_counter || 0, target: targets.closure_counter || 32, yesterday: 0, wtfKey: null },
    { label: 'TOTAL CLOSURES', actual: displayMetrics.total_closures || glance.total_closures || 0, target: targets.total_closures || 80, yesterday: yesterdayData.total_closures || 0, highlight: true, wtfKey: 'closures' },
  ];

  return (
    <div data-testid="command-center-page">
      {/* Header with Title and Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1F4E79]">COMMAND CENTER</h1>
          <p className="text-sm text-gray-500">
            Date: <span className="font-semibold">{today}</span> | 
            Last Updated: <span className="font-semibold">{lastUpdate.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}</span> |
            Auto-refresh: 60s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-40"
            data-testid="date-picker"
          />
          <Button variant="outline" size="sm" onClick={() => fetchData(selectedDate)} data-track="cc-refresh-data">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* FILTERS SECTION */}
      <Card className="mb-6 border-[#1F4E79]">
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter size={18} /> FILTERS
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Manager</Label>
              <Select value={selectedManager} onValueChange={handleManagerChange}>
                <SelectTrigger data-testid="filter-manager">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map(m => (
                    <SelectItem key={m.manager_id} value={m.manager_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Team Leader</Label>
              <Select value={selectedTL} onValueChange={handleTLChange}>
                <SelectTrigger data-testid="filter-tl">
                  <SelectValue placeholder="All TLs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All TLs</SelectItem>
                  {filteredTLs.map(tl => (
                    <SelectItem key={tl.tl_id} value={tl.tl_id}>{tl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Gym</Label>
              <Select value={selectedGym} onValueChange={setSelectedGym}>
                <SelectTrigger data-testid="filter-gym">
                  <SelectValue placeholder="All Gyms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Gyms</SelectItem>
                  {filteredGyms.map(g => (
                    <SelectItem key={g.gym_id} value={g.gym_id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="w-full">
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
              <strong>Filtering:</strong> Showing {filteredGymPerformance.length} gym(s)
              {selectedManager !== 'all' && ` | Manager: ${managers.find(m => m.manager_id === selectedManager)?.name}`}
              {selectedTL !== 'all' && ` | TL: ${teamLeaders.find(t => t.tl_id === selectedTL)?.name}`}
              {selectedGym !== 'all' && ` | Gym: ${gyms.find(g => g.gym_id === selectedGym)?.name}`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* COMMITMENT INSIGHTS SECTION */}
      <Card className="mb-6 border-orange-200">
        <CardHeader className="py-3 bg-orange-500 text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target size={18} /> COMMITMENT INSIGHTS
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-orange-600"
              onClick={() => navigate('/commitment-dashboard')}
            >
              View Details →
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* Today vs Past Breakdown Banner - Clickable */}
          {(commitmentInsights.todayCount > 0 || commitmentInsights.pastCount > 0) && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div
                className="p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:shadow-md transition-all hover:border-blue-400"
                onClick={() => navigate('/commitment-dashboard?dateFilter=today')}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700">Today's Commitments</span>
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-xl font-bold text-blue-800">{commitmentInsights.todayCount}</div>
                {commitmentInsights.todayPendingVerification > 0 && (
                  <div className="text-xs text-blue-600 mt-1">
                    {commitmentInsights.todayPendingVerification} pending verification
                  </div>
                )}
                <div className="text-xs text-blue-500 mt-1">Click to view →</div>
              </div>
              <div
                className={`p-3 rounded-lg cursor-pointer hover:shadow-md transition-all ${commitmentInsights.pastCount > 0 ? 'bg-red-50 border border-red-200 hover:border-red-400' : 'bg-gray-50 border border-gray-200 hover:border-gray-400'}`}
                onClick={() => navigate('/commitment-dashboard?dateFilter=past')}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${commitmentInsights.pastCount > 0 ? 'text-red-700' : 'text-gray-600'}`}>Past Commitments</span>
                  <Clock className={`w-4 h-4 ${commitmentInsights.pastCount > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                </div>
                <div className={`text-xl font-bold ${commitmentInsights.pastCount > 0 ? 'text-red-800' : 'text-gray-600'}`}>{commitmentInsights.pastCount}</div>
                {commitmentInsights.pastPendingVerification > 0 && (
                  <div className="text-xs text-red-600 mt-1 font-medium">
                    ⚠ {commitmentInsights.pastPendingVerification} overdue - verify first!
                  </div>
                )}
                <div className={`text-xs mt-1 ${commitmentInsights.pastCount > 0 ? 'text-red-500' : 'text-gray-500'}`}>Click to view →</div>
              </div>
            </div>
          )}

          {/* Warning banner if there are pending verifications */}
          {commitmentInsights.verifiedByTL > 0 && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-800">
                  {commitmentInsights.verifiedByTL} commitment{commitmentInsights.verifiedByTL !== 1 ? 's' : ''} awaiting Manager verification
                </p>
                <div className="text-xs text-orange-700 mt-1 space-y-1">
                  {Object.entries(commitmentInsights.pendingByManager).map(([manager, count]) => (
                    <div key={manager} className="flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      <span><strong>{manager}</strong> has {count} pending verification{count !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-white border rounded-lg shadow-sm">
              <div className="text-xs text-gray-500 font-medium">Total Expected</div>
              <div className="text-2xl font-bold text-gray-800">{commitmentInsights.totalExpected}</div>
            </div>
            <div className="p-3 bg-white border rounded-lg shadow-sm">
              <div className="text-xs text-gray-500 font-medium">Fulfilled</div>
              <div className="text-2xl font-bold text-green-600">{commitmentInsights.fulfilled}</div>
            </div>
            <div className="p-3 bg-white border rounded-lg shadow-sm">
              <div className="text-xs text-gray-500 font-medium">Revenue Expected</div>
              <div className="text-lg font-bold text-gray-800">₹{(commitmentInsights.revenueExpected || 0).toLocaleString()}</div>
            </div>
            <div className="p-3 bg-white border rounded-lg shadow-sm">
              <div className="text-xs text-gray-500 font-medium">Revenue Done</div>
              <div className="text-lg font-bold text-green-600">₹{(commitmentInsights.revenueDone || 0).toLocaleString()}</div>
            </div>
          </div>

          {/* Verification Status Row - Clickable */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer hover:shadow-md transition-all hover:border-yellow-400"
              onClick={() => navigate('/commitment-dashboard?verificationFilter=verifiedByTL')}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-yellow-700 font-medium">TL Verified Only</div>
                <Clock className="w-4 h-4 text-yellow-600" />
              </div>
              <div className="text-xl font-bold text-yellow-700">{commitmentInsights.verifiedByTL}</div>
              <div className="text-xs text-yellow-600">Awaiting Manager</div>
            </div>
            <div
              className="p-3 bg-orange-50 border border-orange-200 rounded-lg cursor-pointer hover:shadow-md transition-all hover:border-orange-400"
              onClick={() => navigate('/commitment-dashboard?verificationFilter=verifiedByManager')}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-orange-700 font-medium">Manager Only</div>
                <AlertCircle className="w-4 h-4 text-orange-600" />
              </div>
              <div className="text-xl font-bold text-orange-700">{commitmentInsights.verifiedByManager}</div>
              <div className="text-xs text-orange-600">TL verification missing</div>
            </div>
            <div
              className="p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:shadow-md transition-all hover:border-green-400"
              onClick={() => navigate('/commitment-dashboard?verificationFilter=fullyVerified')}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-green-700 font-medium">Fully Verified</div>
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
              <div className="text-xl font-bold text-green-700">{commitmentInsights.fullyVerified}</div>
              <div className="text-xs text-green-600">Ready to fulfill</div>
            </div>
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:shadow-md transition-all hover:border-red-400"
              onClick={() => navigate('/commitment-dashboard?verificationFilter=unverified')}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs text-red-700 font-medium">Unverified</div>
                <XCircle className="w-4 h-4 text-red-600" />
              </div>
              <div className="text-xl font-bold text-red-700">{commitmentInsights.unverified}</div>
              <div className="text-xs text-red-600">Needs both verifications</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROLE-SPECIFIC INSIGHT CARDS */}
      {(userRole === 'admin' || userRole === 'manager') && (
        <Card className="mb-6 border-blue-200 bg-blue-50" data-testid="manager-insights-card">
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={18} /> MANAGER INSIGHTS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Team Performance Overview */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/manager-dashboard')}>
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-sm">Team Performance</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">{roleInsights.tlPerformance.length}</div>
                <p className="text-xs text-gray-600">Team Leaders Active</p>
                {roleInsights.topPerformer && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                    <span className="text-green-700 font-medium">Top: {roleInsights.topPerformer.tl_name}</span>
                    <span className="text-green-600 ml-1">({roleInsights.topPerformer.closures} closures)</span>
                  </div>
                )}
              </div>

              {/* Pending Verifications */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/verification')}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                  <span className="font-semibold text-sm">Pending Verifications</span>
                </div>
                <div className="text-2xl font-bold text-yellow-700">{roleInsights.pendingVerifications}</div>
                <p className="text-xs text-gray-600">Awaiting Review</p>
                <Button variant="link" className="px-0 text-xs h-6" onClick={(e) => { e.stopPropagation(); navigate('/verification'); }} data-track="cc-review-verifications">
                  Review Now →
                </Button>
              </div>

              {/* Problem Gyms */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/command-center')}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-sm">Problem Gyms</span>
                </div>
                <div className="text-2xl font-bold text-red-700">{roleInsights.problemGyms}</div>
                <p className="text-xs text-gray-600">Need Attention</p>
                {roleInsights.needsAttention && (
                  <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                    <span className="text-red-700 font-medium">Alert: {roleInsights.needsAttention.tl_name}</span>
                    <span className="text-red-600 ml-1">({roleInsights.needsAttention.connectRate}% connect)</span>
                  </div>
                )}
              </div>

              {/* TL Comparison */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/manager-dashboard')}>
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-sm">TL Comparison</span>
                </div>
                <div className="space-y-1 mt-2">
                  {roleInsights.tlPerformance.slice(0, 3).map((tl, idx) => (
                    <div key={tl.tl_id || idx} className="flex justify-between text-xs">
                      <span className="truncate w-24">{tl.tl_name}</span>
                      <span className="font-medium" style={{ color: (tl.connectRate || 0) >= 70 ? '#28A745' : (tl.connectRate || 0) >= 60 ? '#FFC107' : '#DC3545' }}>
                        {tl.connectRate || 0}%
                      </span>
                      <span className="text-green-600 font-semibold">{tl.closures || 0}</span>
                    </div>
                  ))}
                </div>
                <Button variant="link" className="px-0 text-xs h-6">View All →</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manager WTF Summary Cards */}
      {(userRole === 'manager') && wtfRoleData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-purple-600 font-medium uppercase">Team WTF Target</p>
              <WtfSyncBadge lastSync={wtfTargetsSync} stale={wtfTargetsStale} size="sm" showLabel={false} />
            </div>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalTarget} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">Team WTF Achieved</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalAchieved} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">Team WTF Walk-ins</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalWalkins} loading={wtfLeadsLoading} stale={wtfLeadsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">Team WTF Remaining</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalRemaining} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
        </div>
      )}

      {userRole === 'team_leader' && (
        <Card className="mb-6 border-green-200 bg-green-50" data-testid="tl-insights-card">
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 size={18} /> MY GYMS TODAY
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Target Progress */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/tl-dashboard')}>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-sm">Target Progress</span>
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {roleInsights.myTargetProgress.closures}/{roleInsights.myTargetProgress.target}
                </div>
                <p className="text-xs text-gray-600">Closures Today</p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, roleInsights.myTargetProgress.target > 0 ? (roleInsights.myTargetProgress.closures / roleInsights.myTargetProgress.target) * 100 : 0)}%` }}
                  />
                </div>
              </div>

              {/* Pending Actions */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/tl-dashboard')}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-sm">Pending Actions</span>
                </div>
                <div className="text-2xl font-bold text-amber-700">{roleInsights.myPendingActions}</div>
                <p className="text-xs text-gray-600">Gyms Need Attention</p>
                <Button variant="link" className="px-0 text-xs h-6" onClick={(e) => { e.stopPropagation(); navigate('/tl-dashboard'); }}>
                  Take Action →
                </Button>
              </div>

              {/* Pulse Status */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/pulse-operations')}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-sm">Pulse Status</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {roleInsights.myPulseStatus.filled}/{roleInsights.myPulseStatus.total}
                </div>
                <p className="text-xs text-gray-600">Pulses Filled</p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${roleInsights.myPulseStatus.total > 0 ? (roleInsights.myPulseStatus.filled / roleInsights.myPulseStatus.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* My Gyms Summary */}
              <div className="bg-white p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/tl-dashboard')}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-sm">My Gyms</span>
                </div>
                <div className="space-y-1 mt-2">
                  {roleInsights.myGyms.slice(0, 3).map((gym, idx) => (
                    <div key={gym.gym_id || idx} className="flex justify-between text-xs">
                      <span className="truncate w-20">{gym.gym_name || gym.name}</span>
                      <span className={`font-medium ${(gym.problems || []).length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {(gym.problems || []).length > 0 ? <XCircle className="w-3 h-3 inline" /> : <CheckCircle2 className="w-3 h-3 inline" />}
                      </span>
                      <span className="text-green-600 font-semibold">{gym.closures || 0}</span>
                    </div>
                  ))}
                </div>
                <Button variant="link" className="px-0 text-xs h-6">View All →</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TL WTF Summary Cards */}
      {userRole === 'team_leader' && wtfRoleData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-purple-600 font-medium uppercase">My WTF Target</p>
              <WtfSyncBadge lastSync={wtfTargetsSync} stale={wtfTargetsStale} size="sm" showLabel={false} />
            </div>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalTarget} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">My WTF Achieved</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalAchieved} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">My WTF Walk-ins</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalWalkins} loading={wtfLeadsLoading} stale={wtfLeadsStale} format="number" />
            </p>
          </div>
          <div className="bg-white rounded-lg border-2 border-purple-200 p-4">
            <p className="text-xs text-purple-600 font-medium uppercase">My WTF Remaining</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfRoleData.totalRemaining} loading={wtfTargetsLoading} stale={wtfTargetsStale} format="number" />
            </p>
          </div>
        </div>
      )}

      {/* SECTION 1: TODAY AT A GLANCE */}
      <Card className="mb-6">
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base">SECTION 1: TODAY AT A GLANCE</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#D6DCE5]">
                <tr>
                  <th className="p-2 text-left border font-bold">Metric</th>
                  <th className="p-2 text-center border font-bold">Actual</th>
                  <th className="p-2 text-center border font-bold">Target</th>
                  <th className="p-2 text-center border font-bold">Gap</th>
                  <th className="p-2 text-center border font-bold">Status</th>
                  <th className="p-2 text-center border font-bold whitespace-nowrap text-purple-700">
                    WTF Actual <WtfSyncBadge lastSync={wtfTargetsSync} stale={wtfTargetsStale} size="sm" showLabel={false} />
                  </th>
                  <th className="p-2 text-center border font-bold text-purple-700">Delta</th>
                  <th className="p-2 text-center border font-bold">Yesterday</th>
                  <th className="p-2 text-center border font-bold">Trend</th>
                </tr>
              </thead>
              <tbody>
                {metricsData.map((metric, idx) => {
                  const gap = metric.actual - metric.target;
                  const status = getStatus(metric.actual, metric.target, metric.isRate);
                  const wtfValue = metric.wtfKey ? wtfAggregates[metric.wtfKey] : null;
                  const isWtfLoading = wtfTargetsLoading || wtfCheckinLoading || wtfLeadsLoading || wtfMemberLoading;
                  const isWtfStale = wtfTargetsStale || wtfCheckinStale || wtfLeadsStale || wtfMemberStale;
                  return (
                    <tr key={idx} className={`${metric.highlight ? 'bg-green-50 font-bold' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className={`p-2 border ${metric.highlight ? 'font-bold text-green-700' : ''}`}>{metric.label}</td>
                      <td className="p-2 border text-center font-semibold">{metric.actual}{metric.isRate ? '%' : ''}</td>
                      <td className="p-2 border text-center text-gray-600">{metric.target}{metric.isRate ? '%' : ''}</td>
                      <td className={`p-2 border text-center font-medium ${gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {gap >= 0 ? '+' : ''}{gap}{metric.isRate ? '%' : ''}
                      </td>
                      <td className="p-2 border text-center text-xl">{status}</td>
                      <td className="p-2 border text-center">
                        {metric.wtfKey ? (
                          <WtfDataColumn value={wtfValue} loading={isWtfLoading} stale={isWtfStale} format={metric.isRate ? 'percent' : 'number'} />
                        ) : (
                          <span className="text-gray-300">--</span>
                        )}
                      </td>
                      <td className="p-2 border text-center">
                        {metric.wtfKey ? (
                          <DiscrepancyBadge reported={metric.actual} wtfActual={wtfValue} threshold={0.15} />
                        ) : null}
                      </td>
                      <td className="p-2 border text-center text-gray-500">{metric.yesterday}{metric.isRate ? '%' : ''}</td>
                      <td className="p-2 border text-center">{getTrend(metric.actual, metric.yesterday)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* CHARTS ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Gym Performance Bar Chart */}
        <Card>
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 size={18} /> GYM PERFORMANCE CHART
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={gymChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="dialed" fill="#3B82F6" name="Dialed" />
                <Bar dataKey="connected" fill="#8B5CF6" name="Connected" />
                <Bar dataKey="closures" fill="#10B981" name="Closures" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Connect Rate Line Chart */}
        <Card>
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp size={18} /> CONNECT RATE BY GYM
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={gymChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Line type="monotone" dataKey="connectPct" stroke="#F59E0B" strokeWidth={2} name="Connect %" dot={{ fill: '#F59E0B' }} />
                {/* Target line at 70% */}
                <Line type="monotone" dataKey={() => 70} stroke="#DC3545" strokeDasharray="5 5" name="Target (70%)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* SECTION 2: PULSE STATUS & CLOSURE BREAKDOWN */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Pulse Status */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base">SECTION 2: PULSE STATUS</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#D6DCE5]">
                <tr>
                  <th className="p-2 text-left border font-bold">Pulse</th>
                  <th className="p-2 text-center border font-bold">Time Window</th>
                  <th className="p-2 text-center border font-bold">Dialed</th>
                  <th className="p-2 text-center border font-bold">Connected</th>
                  <th className="p-2 text-center border font-bold">Closures</th>
                  <th className="p-2 text-center border font-bold">Status</th>
                  <th className="p-2 text-center border font-bold">Entries</th>
                </tr>
              </thead>
              <tbody>
                {pulseStatus.map((pulse, idx) => {
                  const statusIcon = pulse.status === 'Complete' ? '✅' : 
                                     pulse.status === 'In Progress' ? '🟡' : 
                                     pulse.status === 'Missing' ? '🔴' : '⏳';
                  return (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 border font-semibold">{pulse.pulse_name}</td>
                      <td className="p-2 border text-center text-gray-600">{pulse.time_window}</td>
                      <td className="p-2 border text-center">{pulse.dialed}</td>
                      <td className="p-2 border text-center">{pulse.connected}</td>
                      <td className="p-2 border text-center font-semibold text-green-600">{pulse.closures}</td>
                      <td className="p-2 border text-center">
                        <span className="text-xl">{statusIcon}</span>
                        <span className="ml-1 text-xs text-gray-500">{pulse.status}</span>
                      </td>
                      <td className="p-2 border text-center">{pulse.entries || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Closure Breakdown Pie Chart */}
        <Card>
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base">CLOSURE BREAKDOWN</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={closureBreakdownData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {closureBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Phone: {glance.closure_phone || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-purple-500" />
                <span className="text-sm">Counter: {glance.closure_counter || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SECTION 3: GYM PERFORMANCE TABLE */}
      <Card className="mb-6">
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-3">
            SECTION 3: GYM PERFORMANCE & PULSE FILL STATUS ({filteredGymPerformance.length} gyms)
            <WtfSyncBadge lastSync={wtfTargetsSync} stale={wtfTargetsStale} />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#D6DCE5]">
                <tr>
                  <th className="p-2 text-left border font-bold">Gym</th>
                  <th className="p-2 text-left border font-bold">TL</th>
                  <th className="p-2 text-left border font-bold">Manager</th>
                  <th className="p-2 text-center border font-bold">Dialed</th>
                  <th className="p-2 text-center border font-bold">Connected</th>
                  <th className="p-2 text-center border font-bold">Connect %</th>
                  <th className="p-2 text-center border font-bold">Closures</th>
                  <th className="p-2 text-center border font-bold text-purple-700 whitespace-nowrap">WTF Target</th>
                  <th className="p-2 text-center border font-bold text-purple-700 whitespace-nowrap">WTF Achieved</th>
                  <th className="p-2 text-center border font-bold">Commitment</th>
                  <th className="p-2 text-center border font-bold">Fulfilled</th>
                  <th className="p-2 text-center border font-bold">Target</th>
                  <th className="p-2 text-center border font-bold">Gap</th>
                  <th className="p-2 text-center border font-bold">Status</th>
                  <th className="p-2 text-center border font-bold">Issues</th>
                  <th className="p-2 text-center border font-bold">P1</th>
                  <th className="p-2 text-center border font-bold">P2</th>
                  <th className="p-2 text-center border font-bold">P3</th>
                  <th className="p-2 text-center border font-bold">P4</th>
                  <th className="p-2 text-center border font-bold">Fill Rate</th>
                </tr>
              </thead>
              <tbody>
                {filteredGymPerformance.map((gym, idx) => {
                  const target = targets.per_gym_closure_target || 5;
                  const gap = (gym.closures || 0) - target;
                  const connectPct = gym.connect_pct || 0;
                  let status = '✅';
                  if ((gym.closures || 0) === 0 || connectPct < 60) status = '🔴';
                  else if (connectPct < 70 || gap < -10) status = '🟡';
                  
                  return (
                    <tr 
                      key={idx} 
                      className={`${(gym.closures || 0) === 0 ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`}
                      onClick={() => setDrilldownModal({ open: true, gymId: gym.gym_id })}
                      data-testid={`gym-row-${gym.gym_id}`}
                    >
                      <td className="p-2 border font-semibold text-blue-600 hover:underline flex items-center gap-1">
                        {gym.gym}
                        <Eye size={14} className="text-gray-400" />
                      </td>
                      <td className="p-2 border text-gray-600">{gym.tl_name || '-'}</td>
                      <td className="p-2 border text-gray-600">{gym.manager_name || '-'}</td>
                      <td className="p-2 border text-center">{gym.dialed || 0}</td>
                      <td className="p-2 border text-center">{gym.connected || 0}</td>
                      <td className="p-2 border text-center font-medium" style={{ color: connectPct >= 70 ? '#28A745' : connectPct >= 60 ? '#FFC107' : '#DC3545' }}>
                        {connectPct}%
                      </td>
                      <td className="p-2 border text-center font-semibold text-green-700">{gym.closures || 0}</td>
                      <td className="p-2 border text-center">
                        <WtfDataColumn value={(wtfGymLookup[gym.gym_id] || wtfGymLookup[gym.gym] || {}).target} loading={wtfTargetsLoading} stale={wtfTargetsStale} />
                      </td>
                      <td className="p-2 border text-center">
                        <WtfDataColumn value={(wtfGymLookup[gym.gym_id] || wtfGymLookup[gym.gym] || {}).achieved} loading={wtfTargetsLoading} stale={wtfTargetsStale} />
                      </td>
                      <td className="p-2 border text-center">{gym.closure_expected || 0}</td>
                      <td className="p-2 border text-center font-medium" style={{
                        color: (gym.closure_done || 0) >= (gym.closure_expected || 1)
                          ? '#28A745'
                          : (gym.closure_done || 0) > 0 ? '#FFC107' : '#DC3545'
                      }}>
                        {gym.closure_done || 0}/{gym.closure_expected || 0}
                      </td>
                      <td className="p-2 border text-center text-gray-500">{target}</td>
                      <td className={`p-2 border text-center font-medium ${gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {gap >= 0 ? '+' : ''}{gap}
                      </td>
                      <td className="p-2 border text-center text-xl">{status}</td>
                      <td className="p-2 border">
                        <ProblemBadges problems={gym.problems} />
                      </td>
                      <td className="p-2 border text-center">{gym.p1 != null ? (gym.p1 ? '\u2705' : '\u23F3') : '-'}</td>
                      <td className="p-2 border text-center">{gym.p2 != null ? (gym.p2 ? '\u2705' : '\u23F3') : '-'}</td>
                      <td className="p-2 border text-center">{gym.p3 != null ? (gym.p3 ? '\u2705' : '\u23F3') : '-'}</td>
                      <td className="p-2 border text-center">{gym.p4 != null ? (gym.p4 ? '\u2705' : '\u23F3') : '-'}</td>
                      <td className="p-2 border text-center font-medium" style={{
                        color: (gym.fill_rate || 0) === 100 ? '#28A745' :
                               (gym.fill_rate || 0) >= 50 ? '#FFC107' : '#DC3545'
                      }}>
                        {gym.pulses_filled != null ? `${gym.pulses_filled}/4 (${gym.fill_rate || 0}%)` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* SECTION 4: ALERTS & PIPELINE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Alerts */}
        <Card>
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle size={18} /> TOP ALERTS ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-64 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                ✅ No critical alerts
              </div>
            ) : (
              <div className="divide-y">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className={`p-3 ${alert.severity === 'critical' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{alert.severity === 'critical' ? '🔴' : '🟡'}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{alert.gym_name} - {alert.tl_name}</div>
                        <div className="text-xs text-gray-600">{alert.issue}: {alert.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tomorrow's Pipeline */}
        <Card>
          <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base">SECTION 4: TOMORROW'S PIPELINE</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#D6DCE5]">
                <tr>
                  <th className="p-2 text-left border font-bold">Item</th>
                  <th className="p-2 text-center border font-bold">Pending</th>
                  <th className="p-2 text-center border font-bold">Conv. Rate</th>
                  <th className="p-2 text-center border font-bold">Expected</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="p-2 border">Closure Expected (not done)</td>
                  <td className="p-2 border text-center">{pipeline.closure_pending || 0}</td>
                  <td className="p-2 border text-center">60%</td>
                  <td className="p-2 border text-center font-semibold">{Math.round((pipeline.closure_pending || 0) * 0.6)}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-2 border">Visit Scheduled (not done)</td>
                  <td className="p-2 border text-center">{pipeline.visit_pending || 0}</td>
                  <td className="p-2 border text-center">30%</td>
                  <td className="p-2 border text-center font-semibold">{Math.round((pipeline.visit_pending || 0) * 0.3)}</td>
                </tr>
                <tr className="bg-green-50 font-bold">
                  <td className="p-2 border text-green-700">TOTAL PIPELINE</td>
                  <td className="p-2 border text-center">{(pipeline.closure_pending || 0) + (pipeline.visit_pending || 0)}</td>
                  <td className="p-2 border text-center">-</td>
                  <td className="p-2 border text-center text-green-700 text-lg">
                    {Math.round((pipeline.closure_pending || 0) * 0.6) + Math.round((pipeline.visit_pending || 0) * 0.3)}
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Gym Drilldown Modal */}
      <GymDrilldownModal 
        gymId={drilldownModal.gymId}
        date={selectedDate || data?.today}
        open={drilldownModal.open}
        onClose={() => setDrilldownModal({ open: false, gymId: null })}
      />
    </div>
  );
}
